import { useEffect, useMemo, useState } from "react";
import useSeo from "../lib/useSeo";
import galleryImages from "../data/galleryImages";

const SITE_URL = process.env.PUBLIC_URL;
const ASSET_URL = process.env.PUBLIC_URL || "";

export default function Gallery() {
  useSeo({
    title: "Before & After Restoration | Evon Polishing",
    description:
      "Before and after examples of Catholic liturgical metalware restoration, polishing, and plating.",
    canonical: `${SITE_URL}/gallery`,
  });

  const [activeIndex, setActiveIndex] = useState(null);

  const activeSrc = useMemo(() => {
    if (activeIndex === null) return null;
    return `${ASSET_URL}/images/gallery/${galleryImages[activeIndex]}`;
  }, [activeIndex]);

  const closeModal = () => setActiveIndex(null);

  const goPrev = () => {
    setActiveIndex((prev) => {
      if (prev === null) return prev;
      return (prev - 1 + galleryImages.length) % galleryImages.length;
    });
  };

  const goNext = () => {
    setActiveIndex((prev) => {
      if (prev === null) return prev;
      return (prev + 1) % galleryImages.length;
    });
  };

  // Keyboard support + body scroll lock
  useEffect(() => {
    if (activeIndex === null) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [activeIndex]);

  return (
    <>
      <section className="section paper">
        <div className="container">
          <div className="gallery-grid">
            {galleryImages.map((img, index) => (
              <button
                type="button"
                className="gallery-item"
                key={img}
                onClick={() => setActiveIndex(index)}
                aria-label="View image larger"
              >
                <img
                  src={`${ASSET_URL}/images/gallery/${img}`}
                  alt="Liturgical metalware restoration example"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Modal ===== */}
      {activeIndex !== null && (
        <div
          className="gallery-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          onMouseDown={(e) => {
            // close when clicking outside the modal content
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="gallery-modal">
            <button
              type="button"
              className="gallery-modal-close"
              onClick={closeModal}
              aria-label="Close"
            >
              ×
            </button>

            <button
              type="button"
              className="gallery-modal-nav prev"
              onClick={goPrev}
              aria-label="Previous image"
            >
              ‹
            </button>

            <img className="gallery-modal-img" src={activeSrc} alt="" />

            <button
              type="button"
              className="gallery-modal-nav next"
              onClick={goNext}
              aria-label="Next image"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </>
  );
}
